import React from 'react';
import {
  Drawer, Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, Chip, MenuItem, Select, FormControl, Tooltip
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BarChartIcon from '@mui/icons-material/BarChart';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';

interface SidebarProps {
  drawerWidth: number;
  mobileOpen: boolean;
  onClose: () => void;
}

const navItems = [
  { label: 'Dashboard',     icon: <DashboardIcon />,     path: '/dashboard' },
  { label: 'Contracts',     icon: <DescriptionIcon />,   path: '/contracts' },
  { label: 'Obligations',   icon: <AssignmentIcon />,    path: '/obligations' },
  { label: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
  { label: 'Reports',       icon: <BarChartIcon />,      path: '/reports' }
];

const ROLES = ['admin', 'manager', 'editor', 'viewer'] as const;

const ROLE_LABELS: Record<string, string> = {
  admin:   'Admin',
  manager: 'Manager',
  editor:  'Editor',
  viewer:  'Viewer'
};

export default function Sidebar({ drawerWidth, mobileOpen, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, effectiveRole, viewAsRole, setViewAsRole } = useUser();

  // Compute locally for reliability — don't rely on context-passed isAdmin
  const isAdmin = user?.role === 'admin';
  const showAdmin = ['admin', 'manager'].includes(effectiveRole);
  const isImpersonating = viewAsRole !== null;

  // Debug: log context values so we can trace rendering issues
  console.log('[Sidebar] user.role:', user?.role, '| isAdmin:', isAdmin, '| effectiveRole:', effectiveRole);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo / Brand */}
      <Box sx={{
        p: 2, borderBottom: '1px solid', borderColor: 'divider',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5
      }}>
        <Box
          component="img"
          src="/hyperlink-logo.png"
          alt="HyperLink"
          sx={{ width: '80%', maxWidth: 160 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1, fontSize: 10, fontWeight: 600 }}>
          CONTRACT MANAGEMENT
        </Typography>
      </Box>

      {/* "View as" impersonation banner */}
      {isImpersonating && (
        <Box sx={{ px: 1.5, py: 1, bgcolor: 'warning.50', borderBottom: '1px solid', borderColor: 'warning.200' }}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <VisibilityIcon sx={{ fontSize: 14, color: 'warning.dark' }} />
            <Typography variant="caption" fontWeight={600} color="warning.dark">
              Previewing as {ROLE_LABELS[viewAsRole!]}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Main Navigation */}
      <List sx={{ px: 1, py: 1.5, flexGrow: 1 }}>
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); onClose(); }}
                selected={isActive}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #1a6fff 0%, #c42bb0 100%)',
                    color: 'white',
                    '& .MuiListItemIcon-root': { color: 'white' },
                    '&:hover': { background: 'linear-gradient(135deg, #155fe0 0%, #a8239a 100%)' }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* Admin section — only shown when effectiveRole is admin or manager */}
      {showAdmin && (
        <List sx={{ px: 1, py: 1 }}>
          {[{ label: 'Administration', icon: <AdminPanelSettingsIcon />, path: '/admin' }].map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => { navigate(item.path); onClose(); }}
                  selected={isActive}
                  sx={{
                    borderRadius: 2,
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #1a6fff 0%, #c42bb0 100%)',
                      color: 'white',
                      '& .MuiListItemIcon-root': { color: 'white' },
                      '&:hover': { background: 'linear-gradient(135deg, #155fe0 0%, #a8239a 100%)' }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 400 }} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Role impersonation selector — admins only */}
      {isAdmin && (
        <>
          <Divider />
          <Box sx={{ px: 1.5, py: 1.25 }}>
            <Tooltip title="Preview the UI as a different role. Your actual permissions are unchanged." placement="top">
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
                Preview as role
              </Typography>
            </Tooltip>
            <FormControl size="small" fullWidth>
              <Select
                value={viewAsRole ?? ''}
                displayEmpty
                onChange={(e) => setViewAsRole(e.target.value === '' ? null : e.target.value)}
                sx={{ fontSize: 13 }}
              >
                <MenuItem value=""><em>My role (Admin)</em></MenuItem>
                {ROLES.filter(r => r !== 'admin').map(r => (
                  <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      {/* User info at bottom */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {user?.display_name}
        </Typography>
        <Typography variant="caption" color="text.disabled" display="block" noWrap>
          {user?.email}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
          <Chip
            label={isImpersonating ? `${ROLE_LABELS[viewAsRole!]} (preview)` : ROLE_LABELS[user?.role ?? 'viewer']}
            size="small"
            color={isImpersonating ? 'warning' : 'default'}
            sx={{ height: 18, fontSize: 10 }}
          />
        </Box>
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5, fontSize: 10, opacity: 0.6 }}>
          v{new Date().toISOString().slice(0, 10).replace(/-/g, '.')}.{process.env.REACT_APP_BUILD_REV || '01'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}
      >
        {drawer}
      </Drawer>
      {/* Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', borderRight: '1px solid', borderColor: 'divider' }
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
